import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

export type CalendarDefaultView = 'month' | 'week' | 'day' | 'list';
export type WeekStartDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type ColorAppointmentsBy = 'category' | 'provider' | 'status';

export interface CalendarSettingsRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  filter_category_enabled: number | null; // 0/1
  filter_status_enabled: number | null;
  filter_provider_enabled: number | null;
  filter_service_enabled: number | null;
  filter_room_enabled: number | null;
  filter_location_enabled: number | null;
  default_view: string | null;
  week_starts_on: string | null;
  working_hours_start_min: number | null;
  working_hours_end_min: number | null;
  slot_interval_min: number | null;
  color_appointments_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

/**
 * Fetch calendar settings for an org.
 * Returns the org-default row (clinic_id IS NULL).
 */
export function qCalendarSettings(): QuerySpec<[]> {
  return {
    key: ['calendar-settings'] as const,
    sql: `SELECT * FROM appointment_calendar_settings WHERE clinic_id IS NULL ORDER BY updated_at DESC LIMIT 1`,
    params: [],
    watch: ['appointment_calendar_settings'],
  };
}

// ─── Helpers ───

/**
 * Map a CalendarSettingsRow to the UI-friendly shape.
 * Booleans are stored as integers (0/1) in SQLite.
 */
export function mapCalendarSettingsToUI(row: CalendarSettingsRow) {
  return {
    id: row.id,
    categoryFilter: row.filter_category_enabled == 1,
    statusFilter: row.filter_status_enabled == 1,
    providerFilter: row.filter_provider_enabled == 1,
    serviceFilter: row.filter_service_enabled == 1,
    roomFilter: row.filter_room_enabled == 1,
    locationFilter: row.filter_location_enabled == 1,
    defaultView: (row.default_view ?? 'week') as CalendarDefaultView,
    weekStartsOn: (row.week_starts_on ?? 'mon') as WeekStartDay,
    workingHoursStartMin: row.working_hours_start_min ?? 480,
    workingHoursEndMin: row.working_hours_end_min ?? 1200,
    slotIntervalMin: row.slot_interval_min ?? 30,
    colorBy: (row.color_appointments_by ?? 'category') as ColorAppointmentsBy,
  };
}

/**
 * Map UI form values back to the flat column shape for db.execute().
 */
export function mapUIToCalendarSettingsColumns(ui: {
  categoryFilter: boolean;
  statusFilter: boolean;
  providerFilter: boolean;
  serviceFilter: boolean;
  roomFilter: boolean;
  locationFilter: boolean;
  defaultView: string;
  weekStartsOn: string;
  workingHoursStartMin: number;
  workingHoursEndMin: number;
  slotIntervalMin: number;
  colorBy: string;
}) {
  return {
    filter_category_enabled: ui.categoryFilter ? 1 : 0,
    filter_status_enabled: ui.statusFilter ? 1 : 0,
    filter_provider_enabled: ui.providerFilter ? 1 : 0,
    filter_service_enabled: ui.serviceFilter ? 1 : 0,
    filter_room_enabled: ui.roomFilter ? 1 : 0,
    filter_location_enabled: ui.locationFilter ? 1 : 0,
    default_view: ui.defaultView,
    week_starts_on: ui.weekStartsOn,
    working_hours_start_min: ui.workingHoursStartMin,
    working_hours_end_min: ui.workingHoursEndMin,
    slot_interval_min: ui.slotIntervalMin,
    color_appointments_by: ui.colorBy,
    updated_at: new Date().toISOString(),
  };
}
